import cn from "classnames";
import { featureModuleTypesEnum } from "helpers/enums";
import { hiddenUserData } from "helpers/hiddenData";
import { paths } from "helpers/paths";
import { formatValue } from "helpers/reports";
import { useLocalStorage } from "hooks/useLocalStorage";
import upperFirst from "lodash/upperFirst";
import Tooltip from "rc-tooltip";
import React from "react";

export default function TruckBids({ row, isPrintForm, organization, availableFeatureModules }) {
    const [showDatesInUserTimezone, setShowDatesInUserTimezone] = useLocalStorage(
        "overall-report-bid-dates-in-user-timezone",
        false,
    );
    if (!row.original?.bids || row.original.bids.length === 0) {
        return null;
    }

    const tableProps = isPrintForm
        ? {
              className: "p-3 reports--bg-bluelight",
              style: { minWidth: "100%", width: row.cells.reduce((acc, cell) => acc + cell.column.width, 0) },
          }
        : {
              className: "table-responsive",
          };

    return (
        <div>
            <div className="d-flex justify-content-end mb-3">
                <div
                    className="d-flex align-items-center"
                    onClick={() => setShowDatesInUserTimezone(!showDatesInUserTimezone)}
                >
                    <label className="switch mr-2">
                        <span
                            className={cn("switch__slider", { "switch__slider--checked": showDatesInUserTimezone })}
                        />
                    </label>
                    <span className="small cursor-pointer switch__label">Отобразить даты в часовом поясе компании</span>
                </div>
            </div>
            <div {...tableProps}>
                <table className="table table-sm mb-0 border-bottom border-secondary bg-secondary">
                    <thead>
                        <tr className="table-active">
                            <th>#</th>
                            <th>Заявка</th>
                            <th>Контрагент</th>
                            <th>Внешний ID Заявки</th>
                            <th>Маршрут</th>
                            <th>Даты выполнения</th>
                            <th>Активный, км</th>
                            <th>Порожний, км</th>
                            <th>Общий, км</th>
                            <th>ВАЛ</th>
                            <th>Сред. вес, т</th>
                            <th>{upperFirst(organization?.currencyData?.name)}/км</th>
                            {availableFeatureModules &&
                                availableFeatureModules.includes(featureModuleTypesEnum.ProfitabilityOfBid) && (
                                    <>
                                        <th>Количество дней</th>
                                        <th>Цена топлива</th>
                                        <th>Конечная прибыль</th>
                                        <th>Качество</th>
                                    </>
                                )}
                        </tr>
                    </thead>
                    <tbody>
                        {row.original.bids.map((item, index) => (
                            <tr
                                key={index}
                                className={cn({
                                    "r-item__compound-bid": item.compoundBidId,
                                    "r-item__mileage-deviation": item.hasOdometerMileageDeviation,
                                })}
                            >
                                <td>{index + 1}</td>
                                <td data-id={item.id}>
                                    <a target="_blank" rel="noopener noreferrer" href={paths.bidShow("bid", item.id)}>
                                        {item.id}
                                    </a>
                                    {item.hasManualMileage && (
                                        <Tooltip overlay="Закрыта вручную" placement="top">
                                            <span className="pl-1 icon-uEA83-user-edit b-point__tooltip-icon" />
                                        </Tooltip>
                                    )}
                                    {item.compoundBidId && (
                                        <Tooltip overlay="Сборная заявка" placement="top">
                                            <i className="pl-1 b-table__compound-icon icon-uEA76-animation-outline" />
                                        </Tooltip>
                                    )}
                                    {item.isExpressBid && (
                                        <Tooltip overlay="Экспресс-заявка" placement="top">
                                            <i className="pl-1 icon-uEA90-bolt b-point__tooltip-icon" />
                                        </Tooltip>
                                    )}
                                    {item.hasEmulatedMileage && (
                                        <Tooltip overlay="В заявке присутствуют сэмулированные участки маршрута">
                                            <i className="pl-1 icon-uEACB-emulated-route b-point__tooltip-icon" />
                                        </Tooltip>
                                    )}
                                    {item.hasOdometerMileageDeviation && (
                                        <Tooltip overlay="Разница между пробегам по плану и по одометру больше 30%">
                                            <i className="pl-1 icon-uEA86-exclamation-circle text-danger" />
                                        </Tooltip>
                                    )}
                                </td>
                                <td data-counterpartyname={item.id}>
                                    {hiddenUserData(item.counterpartyName, { type: "title" })}
                                </td>
                                <td data-externalid={item.id}>{item.externalId}</td>
                                <td data-route={item.id}>{item.route}</td>
                                <td data-executiondaterange={item.id}>
                                    {showDatesInUserTimezone
                                        ? item.executionDateRangeOrganizationTimezone
                                        : item.executionDateRange}
                                </td>
                                <td data-activemileage={item.id}>{formatValue(item.activeMileage, "double")}</td>
                                <td data-emptymileage={item.id}>{formatValue(item.emptyMileage, "double")}</td>
                                <td data-overallmileage={item.id}>{formatValue(item.overallMileage, "double")}</td>
                                <td data-overallbidprice={item.id}>
                                    {hiddenUserData(formatValue(item.overallBidPrice, "double"), { type: "price" })}
                                </td>
                                <td data-averageplanweight={item.id}>
                                    {formatValue(item.averagePlanWeight, "double")}
                                </td>
                                <td data-output={item.id}>
                                    {hiddenUserData(formatValue(item.output, "double"), { type: "pricePerVal" })}
                                </td>
                                {availableFeatureModules &&
                                    availableFeatureModules.includes(featureModuleTypesEnum.ProfitabilityOfBid) && (
                                        <>
                                            <td data-numberofdays={item.id}>
                                                {hiddenUserData(formatValue(item.numberOfDays, "double"))}
                                            </td>
                                            <td data-fuelcost={item.id}>
                                                {hiddenUserData(formatValue(item.fuelCost, "double"), {
                                                    type: "price",
                                                })}
                                            </td>
                                            <td data-finalprofit={item.id}>
                                                {hiddenUserData(formatValue(item.finalProfit, "double"), {
                                                    type: "price",
                                                })}
                                            </td>
                                            <td data-quality={item.id}>
                                                {hiddenUserData(formatValue(item.quality, "double"))}
                                            </td>
                                        </>
                                    )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
